import React, { useContext, useEffect, useState } from "react";
import { formEdit } from "../../Tools/Tools";
import DayTypeTitle from "./Components/dayTypeComponents/DayTypeTitle";
import DayTypeCheckBox from "./Components/dayTypeComponents/DayTypeCheckBox";
import clsx from "clsx";
import dialogStyles from "./Components/dialogStyles.module.css";
import { DefaultDialogContentProps } from "./DialogAcctools";
import { SmetaModalsButtonsContext } from "../../Tools/modalManager";

const defaultCheckState = {
  Отработанные: 0,
  Неотработанные: 0,
  Обычный: 0,
  Праздничный: 0,
  Выходной: 0,
  ПредПраздничный: 0,
  Понедельник: 0,
  Вторник: 0,
  Среда: 0,
  Четверг: 0,
  Пятница: 0,
  Суббота: 0,
  Воскресенье: 0,
};

const daysData: { [key: number]: string } = {
  0: "Понедельник",
  1: "Вторник",
  2: "Среда",
  3: "Четверг",
  4: "Пятница",
  5: "Суббота",
  6: "Воскресенье",
};
const typesData: { [key: number]: string } = {
  0: "Обычный",
  1: "Праздничный",
  2: "Выходной",
  3: "ПредПраздничный",
};
const statesData: { [key: number]: string } = {
  0: "Отработанные",
  1: "Неотработанные",
};

/**
 * Контент диалога маски типов дней
 *
 * @param {Object} [props] объект с данными для компонента Класификатор
 * @param {Prefixes} [props.Module] модуль компонента
 * @param {string} [props.ObjRef] целое число с зашифрованными чекбоксами
 * @param {string} [props.Name] Данные от форм для форм
 * @param {string} [props.RequestID] ID запроса для цепи запросов
 * @param {string} [props.Buttons] настрока видимых кнопок
 * @param {string} [props.hiddenButton] скрыть панель с кнопками
 * @param {string} [props.record] данные параметров для параметров
 * @param {string} [props.from] данные для цепи запросов
 * @param {string} [props.addInfo] дополнительные данные
 * @param {string} [props.onEdit] функция для кнопки "выбрать"
 * @param {string} [props.setValue] функция обновления состояния внтреннего значения редактора
 * @param {string} [props.setInputValue] функция обновлнния состояния значения редактора
 * @param {string} [props.func] функция для цепи запросов
 *
 * @returns React.JSX.Element
 */
export default function DialogDayType({ props }: DefaultDialogContentProps) {
  const {
    Module: module,
    ObjRef: objRef,
    Name: name,
    RequestID: requestId,
    Buttons: buttons,
    hiddenButton,
    record,
    from,
    addInfo,
    onEdit,
    setValue,
    setInputValue,
    func,
  } = props;

  const [checkValues, setCheckValues] = useState<{ [key: string]: number }>(defaultCheckState);

  const setButtons = useContext(SmetaModalsButtonsContext);

  useEffect(() => {
    const modalButtons: { [key: number]: { label: string; onClick: () => void } } = {};
    if (!buttons || Number(buttons) & 1) {
      modalButtons[1] = { label: "Выбрать", onClick: onSelect };
    }
    setButtons?.(modalButtons);
  }, [setButtons, checkValues]);

  /**
   * Инициализация значений чек-боксов
   */
  const loadCheckValues = () => {
    setCheckValues(getCheckStates(objRef));
  };
  useEffect(loadCheckValues, []);

  /**
   * Парсит число на значения для чекбоксов
   *
   * @param {string} stringCode число в котором зашифрованны значения чекбоксов
   * @returns Возвращает объект с состояниями чек боксов, где ключ название чек бокса, а значение его состояние
   */
  const getCheckStates = (stringCode: string | undefined) => {
    const code = Number(stringCode);
    const result: { [key: string]: number } = {};
    if (!code) return result;

    const days = (code >> 16) & 0b1111111;
    const types = (code >> 24) & 0b1111;
    const states = (code >> 28) & 0b11;

    for (let i = 0; i < 7; i++) {
      result[daysData[i]] = (days >> i) & 0b1;
    }
    for (let i = 0; i < 4; i++) {
      result[typesData[i]] = (types >> i) & 0b1;
    }
    for (let i = 0; i < 2; i++) {
      result[statesData[i]] = (states >> i) & 0b1;
    }
    return result;
  };

  /**
   * Парсит объект состояний чекбоксов и записывает их обратно в целое число
   *
   * @returns Число с зашифрованными в нем чекбоксами
   */
  const getTag = () => {
    let result = 0;

    for (let i = 0; i < 7; i++) {
      result += checkValues[daysData[i]] << (i + 16);
    }
    for (let i = 0; i < 4; i++) {
      result += checkValues[typesData[i]] << (i + 24);
    }
    for (let i = 0; i < 2; i++) {
      result += checkValues[statesData[i]] << (i + 28);
    }
    return result;
  };

  /**
   * Выполняет функцию выбора по intCode или продолжения цепи запросов передав выбранный intCode
   */
  async function onSelect() {
    if (requestId) {
      await formEdit("1", func, { Name: "ObjRef", Value: getTag() }, requestId, from);
      return;
    }

    if (onEdit) {
      onEdit({
        value: "",
        tag: getTag(),
        name: name,
        record: record,
        textchanged: "0",
        setValue: setValue,
        setInputValue: setInputValue,
        type: module,
        requestId: requestId,
        addInfo: addInfo,
        docID: true,
      });
    }
  }

  /**
   *  Обновляет состояние объекта при клике на чек-бокс
   *
   * @param {Object} [data] данные от чек-бокса
   * @param {string} [data.name] имя чек-бокса
   * @param {string | number} [data.CheckState] состояние чек-бокса
   *
   */
  const onEditCheckBox = (data: { name: string; CheckState: string | number }) => {
    checkValues[data.name] = Number(data.CheckState);
    setCheckValues({ ...checkValues });
  };

  return (
    <div className={clsx(dialogStyles.mainDialogBlock)}>
      <div className={clsx(dialogStyles.fullDialogFlexContainer, dialogStyles.dialogDayTypeContainer)}>
        <div className={clsx(dialogStyles.dialogDayTypeItemContainer)}>
          <div className={clsx(dialogStyles.dialogDayTypeStateContainer)}>
            <DayTypeTitle label="Виды дней" />
            <DayTypeCheckBox label="Отработанные" checkValues={checkValues} onEditCheckBox={onEditCheckBox} />
            <DayTypeCheckBox label="Неотработанные" checkValues={checkValues} onEditCheckBox={onEditCheckBox} />
          </div>
          <div className={clsx(dialogStyles.dialogDayTypeTypesContainer)}>
            <DayTypeTitle label="Типы дней" />

            <DayTypeCheckBox label="Обычный" checkValues={checkValues} onEditCheckBox={onEditCheckBox} />
            <DayTypeCheckBox label="Праздничный" checkValues={checkValues} onEditCheckBox={onEditCheckBox} />
            <DayTypeCheckBox label="Выходной" checkValues={checkValues} onEditCheckBox={onEditCheckBox} />
            <DayTypeCheckBox label="ПредПраздничный" checkValues={checkValues} onEditCheckBox={onEditCheckBox} />
          </div>
        </div>
        <div className={clsx(dialogStyles.dialogDayTypeItemContainer)}>
          <div className={clsx(dialogStyles.dialogDayTypeDaysContainer)}>
            <DayTypeTitle label="Дни недели" />
            <DayTypeCheckBox label="Понедельник" checkValues={checkValues} onEditCheckBox={onEditCheckBox} />
            <DayTypeCheckBox label="Вторник" checkValues={checkValues} onEditCheckBox={onEditCheckBox} />
            <DayTypeCheckBox label="Среда" checkValues={checkValues} onEditCheckBox={onEditCheckBox} />
            <DayTypeCheckBox label="Четверг" checkValues={checkValues} onEditCheckBox={onEditCheckBox} />
            <DayTypeCheckBox label="Пятница" checkValues={checkValues} onEditCheckBox={onEditCheckBox} />
            <DayTypeCheckBox label="Суббота" checkValues={checkValues} onEditCheckBox={onEditCheckBox} />
            <DayTypeCheckBox label="Воскресенье" checkValues={checkValues} onEditCheckBox={onEditCheckBox} />
          </div>
        </div>
      </div>

      {/* <div className={clsx(dialogStyles.dialogButtonsContainer, hiddenButton && dialogStyles.dialogHidden)}>
        <DialogButtons onSelect={onSelect} buttons={buttons} />
      </div> */}
    </div>
  );
}
